/* eslint-disable i18n/no-russian-character */
'use client';

import * as Sentry from '@sentry/nextjs';
import { useEffect, useState } from 'react';

import { GTMErrorPage } from '@/fo/api/gtm';

import { rfTone } from './fonts';

const isDev = process.env.NODE_ENV !== 'production';
const MAX_RETRY_COUNT = 3;
const RETRY_DELAY = 300;
const RETRY_KEY = 'global_error_retry_count';

interface GlobalErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

export default function GlobalError({ error }: GlobalErrorProps) {
  const [retriesExhausted, setRetriesExhausted] = useState(isDev);

  useEffect(() => {
    Sentry.captureException(error);
    // eslint-disable-next-line
    console.error(error);

    if (isDev) return;

    let retryCount = 0;

    try {
      retryCount = Number(sessionStorage.getItem(RETRY_KEY) || '0');
      if (!Number.isFinite(retryCount)) retryCount = 0;
    } catch {
      setRetriesExhausted(true);
      return;
    }

    if (retryCount >= MAX_RETRY_COUNT) {
      setRetriesExhausted(true);
      return;
    }

    try {
      sessionStorage.setItem(RETRY_KEY, String(retryCount + 1));
    } catch {
      setRetriesExhausted(true);
      return;
    }

    const t = setTimeout(() => {
      window.location.reload();
    }, RETRY_DELAY);

    return () => clearTimeout(t);
  }, [error]);

  const onReset = () => {
    try {
      sessionStorage.removeItem(RETRY_KEY);
    } catch {
      // storage может быть недоступен
    }

    window.location.reload();
  };

  return (
    <html lang="ru">
      <body
        className={rfTone.className}
        style={{
          margin: 0,
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f2f6f7',
          color: '#000'
        }}
      >
        <div style={{ textAlign: 'center', padding: 24 }}>
          <h1
            style={{
              margin: '0 0 8px',
              fontSize: 32,
              fontWeight: 600,
              lineHeight: 1.2
            }}
          >
            Ошибка сервера
          </h1>
          <p
            style={{
              margin: '0 0 24px',
              fontSize: 16,
              fontWeight: 300,
              lineHeight: 1.5
            }}
          >
            Не удалось загрузить страницу
          </p>
          <button
            type="button"
            onClick={onReset}
            style={{
              height: 50,
              padding: '0 28px',
              border: 0,
              background: '#072939',
              color: '#fff',
              font: 'inherit',
              fontSize: 16,
              fontWeight: 600,
              lineHeight: 1.5,
              textTransform: 'uppercase',
              cursor: 'pointer'
            }}
            onMouseEnter={(event) => {
              event.currentTarget.style.background = '#00455e';
            }}
            onMouseLeave={(event) => {
              event.currentTarget.style.background = '#072939';
            }}
          >
            Обновить страницу
          </button>
        </div>
        {retriesExhausted && <GTMErrorPage />}
      </body>
    </html>
  );
}
